import { Box, Divider, Grid } from '@mui/material';
import { showAccess, showApproach, showBoneGraft, showComplications, showDecompressionLevels, showDurationOfProcedure, showDurationOfRadiation, showExtensionToPelvis, showFindings, showFusion, showFusionLevels, showImplant, showImplantType, showIndication, showInterbodyFusion, showLocation, showMinimallyInvasive, showMorphogenicProtein, showNavigation, showNerve, showOsteotomy, showOtherVendors, showOutcome, showProcedure, showRobotic, showSide, showTarget, showTiciGrade, showVascularClosureDevice, showVendors } from 'common/caseHelpers';
import { dateToString, toTitleCase } from 'common/utils';
import CaseInfoItem from 'components/case/CaseInfoItem/CaseInfoItem';
import CustomText from 'components/common/CustomText/CustomText';
import { useAuth } from 'context/AuthContext';
import React, { useMemo } from 'react'
import { colors } from 'styles/colors';
import useGlobalStyles from 'styles/globalStyles';

interface Props {
  data: any;
}

export default function ProcedureDetailsSection({ data }: Props) {
  const { user } = useAuth();
  const speciality = user.user_profile[0].speciality?.name;
  const globalStyles = useGlobalStyles();

  const patientName = useMemo(() => {
    return data ? `${data?.patient_last_name}, ${data?.patient_first_name}` : '';
  }, [data]);

  const fields = useMemo(
    () => [
      {
        label: 'Date of Procedure',
        value: dateToString(new Date(data.date_of_procedure)),
        show: true,
      },
      {
        label: 'Procedure Name',
        value: data.procedure_name,
        show: true,
      },
      {
        label: 'Procedure Type',
        value: data.type,
        show: true,
      },
      {
        label: 'Approach',
        value: data.approach,
        show: showApproach(data),
      },
      {
        label: 'Position',
        value: data.position,
        show: showApproach(data),
      },
      {
        label: 'Minimally Invasive',
        value: data.minimally_invasive,
        show: showMinimallyInvasive(data, speciality),
      },
      {
        label: 'Navigation',
        value: data.navigation,
        show: showNavigation(data, speciality),
      },
      {
        label: 'Robotic',
        value: data.robotic,
        show: showRobotic(data, speciality),
      },
      {
        label: 'Indication',
        value: data.indication,
        show: showIndication(data, speciality),
      },
      {
        label: 'Side',
        value: data.side,
        show: showSide(speciality),
      },
      {
        label: 'Target',
        value: data.target,
        show: showTarget(data, speciality),
      },
      {
        label: 'Nerve',
        value: data.nerve,
        show: showNerve(data, speciality),
      },
      {
        label: 'Location',
        value: data.location,
        show: showLocation(data, speciality),
      },
      {
        label: 'Procedure',
        value: data.procedure,
        show: showProcedure(data, speciality),
      },
      {
        label: 'TICI Grade',
        value: data.tici_grade,
        show: showTiciGrade(data, speciality),
      },
      {
        label: 'Osteotomy',
        value: data.osteotomy,
        show: showOsteotomy(data, speciality),
      },
      {
        label: 'Fusion',
        value: data.fusion,
        show: showFusion(data, speciality),
      },
      {
        label: 'Interbody Fusion',
        value: data.interbody_fusion,
        show: showInterbodyFusion(data, speciality),
      },
      {
        label: 'Extension To Pelvis',
        value: data.extension_to_pelvis,
        show: showExtensionToPelvis(data, speciality),
      },
      {
        label: 'Fusion Levels',
        value: data.fusion_levels,
        show: showFusionLevels(data, speciality),
      },
      {
        label: 'Decompression Levels',
        value: data.decompression_levels,
        show: showDecompressionLevels(data, speciality),
      },
      {
        label: 'Morphogenic Protein',
        value: data.morphogenic_protein,
        show: showMorphogenicProtein(data, speciality),
      },
      {
        label: 'Bone Graft',
        value: data.bone_graft,
        show: showBoneGraft(data, speciality),
      },
      {
        label: 'Implant',
        value: data.implant,
        show: showImplant(data, speciality),
      },
      {
        label: 'Implant Type',
        value: data.implant_type,
        show: showImplantType(data, speciality),
      },
      {
        label: 'Vendors',
        value: data._vendors,
        show: showVendors(data, speciality),
      },
      {
        label: 'Other Vendors',
        value: data.vendors,
        show: showOtherVendors(data),
      },
      {
        label: 'Access',
        value: data.access,
        show: showAccess(speciality),
      },
      {
        label: 'Vascular closure device',
        value: data.vascular_closure_device,
        show: showVascularClosureDevice(speciality),
      },
      {
        label: 'Duration of Radiation',
        value: data.duration_of_radiation,
        show: showDurationOfRadiation(data, speciality),
      },
      {
        label: 'Duration of Procedure',
        value: data.duration_of_procedure,
        show: showDurationOfProcedure(speciality),
      },
      {
        label: 'Findings',
        value: data.findings,
        show: showFindings(speciality),
      },
      {
        label: 'Complications',
        value: data.complications,
        show: showComplications(speciality),
      },
      {
        label: 'Outcome',
        value: data.outcome,
        show: showOutcome(speciality),
      },
    ],
    [data, speciality]
  );

  const visibleFields = useMemo(
    () => fields.filter(field => field.show),
    [fields]
  );


  return (
    <Box className={globalStyles.sectionContainer}>
      <Box className={globalStyles.sectionHeader}>
        <CustomText
          text="Procedure Details"
          color={colors.primary}
          fontWeight={600}
        />
      </Box>

      <Divider />

      <Grid container p={3} spacing={1.5}>
        {visibleFields.map((field, index) => (
          <React.Fragment key={field.label}>
            <Grid size={{ xs: 12, md: 6 }}>
              <CaseInfoItem
                label={field.label}
                value={field.value}
              />
            </Grid>

            {(index % 2 === 1 && index < visibleFields.length - 1) && (
              <Grid size={12}>
                <Divider />
              </Grid>
            )}
          </React.Fragment>
        ))}
      </Grid>


    </Box>
  )
}
